import * as Sentry from '@sentry/react';

import {
  SESSION_COOKIE_NAME,
  SESSION_HEADER_NAME,
} from './sessionConstants.js';

type LogContext = {
  [key: string]: unknown;
};

const SESSION_COOKIE_PATTERN = new RegExp(
  `(?:^|; )${SESSION_COOKIE_NAME}=([^;]+)`
);
const SESSION_STORAGE_KEY = `${SESSION_COOKIE_NAME}:session-storage`;

function canUseCrossSiteCookies(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    window.location.protocol === 'https:'
  );
}

function createSessionId(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) =>
    b.toString(16).padStart(2, '0')
  ).join('');
}

function writeSessionToStorage(id: string): void {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  } catch {
    // Ignore storage write failures in restricted environments.
  }
}

function readSessionFromStorage(): string | null {
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function buildSessionCookieValue(id: string): string {
  const attributes = ['path=/'];
  if (canUseCrossSiteCookies()) {
    attributes.push('SameSite=None', 'Secure');
  } else {
    attributes.push('SameSite=Lax');
  }
  return `${SESSION_COOKIE_NAME}=${encodeURIComponent(id)}; ${attributes.join('; ')}`;
}

export function getSessionId(): string | null {
  const match = document.cookie.match(SESSION_COOKIE_PATTERN);
  if (match) {
    const cookieSessionId = decodeURIComponent(match[1]);
    writeSessionToStorage(cookieSessionId);
    return cookieSessionId;
  }
  return readSessionFromStorage();
}

export function resetSession(): string {
  const id = createSessionId();
  writeSessionToStorage(id);
  document.cookie = buildSessionCookieValue(id);
  return id;
}

export function getSessionHeaders(): Record<string, string> {
  const id = getSessionId();
  return id ? { [SESSION_HEADER_NAME]: id } : {};
}

export class ClientLogger {
  sessionId: string | null = null;

  setSessionId(sessionId: string) {
    this.sessionId = sessionId;
  }

  info(message: string, context?: LogContext) {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`[INFO][${this.sessionId}] ${message}`, {
        context: context ? JSON.stringify(context) : '',
      });
    }
    Sentry.logger.info(`[INFO][${this.sessionId}] ${message}`, {
      context: context ? JSON.stringify(context) : '',
    });
  }

  warn(message: string, context?: LogContext) {
    Sentry.logger.warn(`[WARN][${this.sessionId}] ${message}`, {
      context: context ? JSON.stringify(context) : '',
    });
  }

  error(message: string, error?: Error, context?: LogContext) {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.error(error);
      }
      Sentry.logger.error(`[ERROR][${this.sessionId}] ${message}`, {
        error: error?.message || '',
        context: context ? JSON.stringify(context) : '',
      });

      // Send to Sentry with context
      Sentry.withScope((scope: Sentry.Scope) => {
        // Set tags for better filtering and querying
        if (context?.sessionId || this.sessionId) {
          scope.setTag(
            'browser_session_id',
            (context?.sessionId as string) || this.sessionId
          );
        }
        // Set extra context
        scope.setExtras({
          ...context,
          originalMessage: message,
        });

        if (error) {
          Sentry.captureException(error);
        } else {
          Sentry.captureMessage(message, 'error');
        }
      });
    } catch (error) {
      console.error('Error capturing exception', error);
      Sentry.captureException(error);
    }
  }

  debug(message: string, context?: LogContext) {
    if (process.env.NODE_ENV !== 'production') {
      Sentry.logger.debug(`[DEBUG] ${message}`, {
        context: context ? JSON.stringify(context) : '',
      });
    }
  }
}
